import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import HamburgerMenu from "@/components/HamburgerMenu";
import Logo from "@/components/Logo";

// Minimal inline email auto-linker — splits a string on the first
// email address and wraps it in a `mailto:` anchor so the Contact
// page shows a clickable email without needing a full markdown stack.
const EMAIL_SPLIT_RE = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
const EMAIL_TEST_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
function linkifyEmails(text) {
    if (!text) return text;
    const parts = text.split(EMAIL_SPLIT_RE);
    return parts.map((part, idx) =>
        EMAIL_TEST_RE.test(part) ? (
            <a
                key={idx}
                href={`mailto:${part}`}
                className="text-[#0A0A0A] underline-offset-2 hover:underline"
                data-testid={`mailto-${part}`}
            >
                {part}
            </a>
        ) : (
            part
        ),
    );
}

const COPY = {
    "/about": {
        title: "About Us",
        body: [
            "Piqpath is an experiment in generative-UI travel search. Instead of cluttered lists of links, our AI assistant reads your intent and assembles the most useful cards — flights, hotels, holiday packages — right inside the conversation.",
            "Tagline: Your trip. Your way.",
            "Built with FastAPI, React, MongoDB and Gemini.",
        ],
    },
    "/faq": {
        title: "FAQ",
        body: [
            "Q. Are these real flights and hotels?\nA. Flights and hotels surface live partner inventory wherever Travelpayouts has coverage; for everything else (and for holiday packages) we use a Gemini Google-Search-grounded fallback that returns real-world results from public travel sites.",
            "Q. Why does the price look strange for my country?\nA. Prices show in the local currency for domestic routes, and for stays/packages in the destination's local currency. International flights show in your selected currency from the header. The dropdown also retargets the affiliate region and the deals rail.",
            "Q. Where does the data go?\nA. Your chat history is stored anonymously by session ID; affiliate clicks are logged with rough geo only. No personal data is collected.",
        ],
    },
    "/terms": {
        title: "Terms & Conditions",
        body: [
            "Piqpath is provided as-is for evaluation purposes. Booking links resolve to placeholder affiliate URLs and do not constitute commercial offers.",
            "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.",
            "Use of the service implies acceptance of these placeholder terms. The final terms will be published before any commercial launch.",
        ],
    },
    "/privacy": {
        title: "Privacy Policy",
        body: [
            "Effective date: May 4, 2026.\n\nPiqpath (\"we\", \"us\") operates a generative-UI travel-search assistant available on the web at https://piqpath.com and as the \"Piqpath\" Android app. This page explains what we collect, how we use it, and the choices you have. It is the canonical privacy policy referenced by the Android app's Play Store listing and the Cuelinks / Travelpayouts / Awin / Admitad / Agoda channel-verification reviews.",

            "1. Account data we collect\nIf you sign in (optional — chat works anonymously), we store your email address and display name. Authentication runs through standard JWT sessions; passwords are hashed with bcrypt and never stored in plaintext. We do not store passwords for users who sign in with Google.",

            "2. Chat content\nWe store the messages you send and the assistant's replies (including the structured travel cards rendered in the conversation) so the chat survives reload and so collaborative share-links work. Chat content is keyed to your session ID (or your account, when signed in) and is never used to train any model.",

            "3. Location\nApproximate location: we read your public IP at request time to choose your default currency and surface region-relevant deals. This is per-request only — we do not maintain a location history.\nPrecise location: when (and only when) you tap the Location button in the composer, the app reads your device GPS once to recommend flights / stays / things-to-do near you. Coordinates are sent to our reverse-geocoder to resolve the nearest IATA airport and are not retained beyond that single request. We never enable continuous tracking.",

            "4. Affiliate redirects\nWhen you click a \"Book Now\" button, we redirect you to one of our affiliate partners — Travelpayouts (incl. Aviasales, Hotellook), Awin, Cuelinks (incl. GoIndigo for IndiGo flights), Viator, Klook, Singapore Airlines, or Agoda. Each partner has its own privacy policy that governs your visit to their site. We log the click (item ID, partner, rough geo, timestamp) for analytics and commission reconciliation.",

            "5. Third-party AI services\nThe assistant is powered by third-party large-language-model providers reached via the Emergent Universal AI Key. Your message and short context window are sent to those providers solely to generate a reply. We do not send your account email, address, or precise location to AI providers.",

            "6. Cookies & local storage\nWe use browser localStorage for your session ID, language preference, currency selection, and conversation-mode toggle. We do not use third-party advertising cookies. Affiliate partners may set cookies on their own domains after you click through to them.",

            "7. Data retention\nChat sessions are retained until you delete them or close your account. Deleting your account removes your messages, share-tokens, and collaborator memberships. Affiliate-click logs are aggregated to roughly geographic level after 90 days.",

            "8. Sharing & disclosure\nWe do not sell or rent personal data to anyone. We disclose data only to the affiliate partners listed above (when you click their links), to our infrastructure providers under contract, or where compelled by law.",

            "9. Children\nPiqpath is not directed at children under 13 (or under 16 in the EEA / UK). If we learn we have collected personal data from a child, we delete it.",

            "10. Your rights\nYou can request a copy of, correction of, or deletion of your personal data at any time. The fastest path is the in-app **Delete account** button (avatar menu → Delete account) or the public deletion page at https://piqpath.com/delete-account — both call our DELETE /api/me endpoint and remove your account, chat sessions, share links, and collaborator memberships immediately. You may also email connect@piqpath.com for a manual request; we respond within 30 days. EEA / UK / California residents have additional rights under GDPR / CCPA which we honour on request.",

            "11. Security\nData in transit uses TLS 1.2+. Service-account credentials and AI keys are kept in environment variables and dedicated secret stores, never in source control.",

            "12. Changes\nIf we materially change this policy we'll update the effective date above and surface a notice in the app and on this page. Continued use after a change indicates acceptance.",

            "13. Contact\nQuestions, requests, or partner-verification: connect@piqpath.com. Postal: Piqpath, c/o the address registered with our payment processor (available on request).",
        ],
    },
    "/contact": {
        title: "Contact",
        body: [
            "Contact Email: connect@piqpath.com",
            "Have feedback, want to integrate a provider, or verifying channel ownership (Cuelinks, Admitad, Agoda, Travelpayouts)? Email us directly — we read every message.",
            "We're a small team building generative-UI travel experiences.",
        ],
    },
};

export default function StaticPage({ path }) {
    const data = COPY[path] || COPY["/about"];
    return (
        <div className="flex min-h-screen flex-col bg-white text-[#0A0A0A]">
            <header className="sticky top-0 z-10 border-b border-[#E5E5E5] bg-white/90 backdrop-blur">
                <div className="mx-auto flex w-full max-w-[820px] items-center justify-between px-5 py-3">
                    <div className="flex items-center gap-2">
                        <HamburgerMenu />
                        <Logo size="sm" />
                    </div>
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-3 py-1 text-[12px] text-[#0A0A0A] transition hover:border-[#0A0A0A] hover:bg-[#F5F5F5]"
                        data-testid="back-to-chat"
                    >
                        <ArrowLeft size={12} strokeWidth={2.25} /> Back to chat
                    </Link>
                </div>
            </header>

            <main className="mx-auto w-full max-w-[820px] flex-1 px-5 py-16">
                <Logo size="lg" />
                <h1
                    className="mt-10 text-4xl font-semibold tracking-tight sm:text-5xl"
                    data-testid={`static-title-${data.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                    {data.title}
                </h1>
                <div className="mt-8 space-y-6">
                    {data.body.map((p, i) => (
                        <p
                            key={i}
                            className="whitespace-pre-line text-[15px] leading-relaxed text-[#0A0A0A]"
                        >
                            {linkifyEmails(p)}
                        </p>
                    ))}
                </div>
            </main>

            <footer
                className="border-t border-[#E5E5E5] py-6 text-center text-[11px] text-[#A3A3A3]"
                data-testid="static-footer"
            >
                <div className="space-x-3">
                    <Link to="/privacy" className="hover:text-[#0A0A0A]" data-testid="static-footer-privacy">
                        Privacy
                    </Link>
                    <Link to="/terms" className="hover:text-[#0A0A0A]" data-testid="static-footer-terms">
                        Terms
                    </Link>
                    <Link to="/contact" className="hover:text-[#0A0A0A]" data-testid="static-footer-contact">
                        Contact
                    </Link>
                    <Link to="/delete-account" className="hover:text-[#0A0A0A]" data-testid="static-footer-delete">
                        Delete account
                    </Link>
                </div>
                <div className="mt-2">Piqpath · Your trip. Your way.</div>
            </footer>
        </div>
    );
}
